import { join } from 'juriquery'

import { Query } from '../types'

interface PaginationWithSearchUrlQuery {
  page?: string | number
  limit?: string | number
  search?: string
}

interface PaginationWithSearchAndDateRangesUrlQuery
  extends PaginationWithSearchUrlQuery {
  date_from?: string
  date_to?: string
  entry_id?: string
  parameter_ids?: string
  cycle?: string
  incompleted_entries?: 'true' | 'false'
}

interface ExportUrlQuery {
  start?: string
  end?: string
  entry_id?: string
  parameter_ids?: string
  incompleted_entries?: 'true' | 'false'
}

export type GetTrackingTableRouteQuery =
  PaginationWithSearchAndDateRangesUrlQuery
export type GetTrackingTableExportRouteQuery = ExportUrlQuery

export const TRACKING_ROOT_ROUTE = '/tracking'
export const TRACKING_ROUTE = `${TRACKING_ROOT_ROUTE}/clinics/:clinicId`

export const TRACKING_TABLES_ROUTE = `tables`
export const TRACKING_TABLES_ADD_ROUTE = `add`

export const TRACKING_TABLE_ROUTE = `:tableId`
export const TRACKING_TABLE_ADD_ROUTE = `add`
export const TRACKING_TABLE_ADD_WITH_CYCLES_ROUTE = `add-with-cycles`
export const TRACKING_TABLE_EXPORT_ROUTE = `export`
export const TRACKING_TABLE_EDIT_ROUTE = `edit`
export const TRACKING_TABLE_EDIT_CYCLES_ROUTE = `cycles`
export const TRACKING_TABLE_EDIT_CYCLE_EDIT_ROUTE = `:cycleId/edit`
export const TRACKING_TABLE_EDIT_FOLLOW_UPS_ROUTE = `follow-ups`
export const TRACKING_TABLE_ENTRY_EDIT_ROUTE = `:entryId/edit`

export const getTrackingRootRoute = (clinicId: number) =>
  TRACKING_ROUTE.replace(':clinicId', `${clinicId}`)

export const getTrackingRoute = (clinicId: number, query?: Query) =>
  join(getTrackingRootRoute(clinicId), query)

export const getTrackingTablesRoute = (
  clinicId: number,
  query?: Query,
): string =>
  join(`${getTrackingRootRoute(clinicId)}/${TRACKING_TABLES_ROUTE}`, query)

const getRouteWithInjectedTableId = (route: string, tableId: number) =>
  route.replace(':tableId', `${tableId}`)

export const getTrackingTableRoute = (
  clinicId: number,
  tableId: number,
  query?: GetTrackingTableRouteQuery,
): string =>
  join(
    `${getTrackingTablesRoute(clinicId)}/${getRouteWithInjectedTableId(
      TRACKING_TABLE_ROUTE,
      tableId,
    )}`,
    query || {},
  )

export const getTrackingTablesAddRoute = (clinicId: number) =>
  `${getTrackingTablesRoute(clinicId)}/${TRACKING_TABLES_ADD_ROUTE}`

export const getTrackingTableEditRoute = (clinicId: number, tableId: number) =>
  `${getTrackingTableRoute(clinicId, tableId)}/${TRACKING_TABLE_EDIT_ROUTE}`

export const getTrackingTableEditCyclesRoute = (
  clinicId: number,
  tableId: number,
) =>
  `${getTrackingTableEditRoute(
    clinicId,
    tableId,
  )}/${TRACKING_TABLE_EDIT_CYCLES_ROUTE}`

export const getTrackingTableEditFollowUpsRoute = (
  clinicId: number,
  tableId: number,
) =>
  `${getTrackingTableEditRoute(
    clinicId,
    tableId,
  )}/${TRACKING_TABLE_EDIT_FOLLOW_UPS_ROUTE}`

const getRouteWithInjectedCycleId = (route: string, cycleId: number) =>
  route.replace(':cycleId', `${cycleId}`)

export const getTrackingTableEditCycleEditRoute = (
  clinicId: number,
  tableId: number,
  cycleId: number,
) =>
  `${getTrackingTableEditCyclesRoute(
    clinicId,
    tableId,
  )}/${getRouteWithInjectedCycleId(
    TRACKING_TABLE_EDIT_CYCLE_EDIT_ROUTE,
    cycleId,
  )}`

export const getTrackingTableAddRoute = (
  clinicId: number,
  tableId: number,
  query?: GetTrackingTableRouteQuery,
) =>
  join(
    `${getTrackingTableRoute(clinicId, tableId)}/${TRACKING_TABLE_ADD_ROUTE}`,
    query,
  )

export const getTrackingTableExportRoute = (
  clinicId: number,
  tableId: number,
  query?: GetTrackingTableExportRouteQuery,
) =>
  join(
    `${getTrackingTableRoute(
      clinicId,
      tableId,
    )}/${TRACKING_TABLE_EXPORT_ROUTE}`,
    query,
  )

export const getTrackingTableAddWithCyclesRoute = (
  clinicId: number,
  tableId: number,
  query?: GetTrackingTableRouteQuery,
) =>
  join(
    `${getTrackingTableRoute(
      clinicId,
      tableId,
    )}/${TRACKING_TABLE_ADD_WITH_CYCLES_ROUTE}`,
    query,
  )

const getRouteWithInjectedEntryId = (route: string, entryId: number) =>
  route.replace(':entryId', `${entryId}`)

export const getTrackingTableEntryEditRoute = (
  clinicId: number,
  tableId: number,
  entryId: number,
  query?: GetTrackingTableRouteQuery,
) =>
  join(
    `${getTrackingTableRoute(clinicId, tableId)}/${getRouteWithInjectedEntryId(
      TRACKING_TABLE_ENTRY_EDIT_ROUTE,
      entryId,
    )}`,
    query,
  )
